import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kbApi } from '../api/kb';
import { useCompany } from '../contexts/CompanyContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  ThumbsUp, ThumbsDown, Pencil, Trash2,
  Lock, Globe, MessageSquare, Send, X
} from 'lucide-react';
import type { ArticleNote } from '../types';
import { useConfirm } from '../components/ConfirmDialog';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const { can, isStaff, isAdmin } = usePermissions();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [noteContent, setNoteContent] = useState('');

  const companyKey = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['kb-article', slug, companyKey],
    queryFn: () => kbApi.getBySlug(slug!),
    enabled: !!slug && !!currentCompany,
  });

  const articleId = (a: any) => a?.id ?? a?._id;

  const { data: notes = [] } = useQuery({
    queryKey: ['kb-notes', articleId(article)],
    queryFn: () => kbApi.getNotes(articleId(article)),
    enabled: isStaff && !!article,
  });

  const feedbackMutation = useMutation({
    mutationFn: (helpful: boolean) => kbApi.feedback(articleId(article), helpful),
    onSuccess: (_, helpful) => {
      toast.success(helpful ? 'Thanks for your feedback!' : 'Feedback recorded');
      queryClient.invalidateQueries({ queryKey: ['kb-article', slug] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => kbApi.delete(articleId(article)),
    onSuccess: () => {
      toast.success('Article deleted');
      queryClient.invalidateQueries({ queryKey: ['kb'] });
      navigate('/kb');
    },
    onError: () => toast.error('Failed to delete article'),
  });

  const addNoteMutation = useMutation({
    mutationFn: () => kbApi.addNote(articleId(article), noteContent.trim()),
    onSuccess: () => {
      setNoteContent('');
      queryClient.invalidateQueries({ queryKey: ['kb-notes', articleId(article)] });
      toast.success('Note added');
    },
    onError: () => toast.error('Failed to add note'),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => kbApi.deleteNote(articleId(article), noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-notes', articleId(article)] });
    },
    onError: () => toast.error('Failed to delete note'),
  });

  const handleDelete = async () => {
    if (await confirm({
      title: 'Delete article',
      message: 'Are you sure you want to delete this article? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    })) {
      deleteMutation.mutate();
    }
  };

  if (!slug) return (
    <div className="flex flex-col gap-4 pb-10">
      <div className="text-err">Invalid article</div>
    </div>
  );
  if (isLoading) return (
    <div className="flex flex-col gap-4 pb-10">
      <div className="py-12 text-center text-muted">Loading article...</div>
    </div>
  );
  if (error || !article) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="py-12 text-center">
          <p className="text-err mb-4">Article not found</p>
          <Link to="/kb" className="btn btn-secondary">Back to Knowledge Base</Link>
        </div>
      </div>
    );
  }

  const a = article as any;
  const isInternal = article.visibility === 'internal';

  return (
    <div className="flex flex-col gap-4 pb-10">
      {can('edit', 'knowledgeBase') && (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/kb/${article.slug}/edit`}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </Link>
          {can('delete', 'knowledgeBase') && (
            <button
              className="btn btn-danger inline-flex items-center gap-2"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4" />
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      )}

      <article className="max-w-3xl card p-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="page-title">{article.title}</h1>
          <div className="flex items-center gap-2 shrink-0 mt-1">
            {isInternal ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-warn/15 text-warn">
                <Lock className="w-3 h-3" />
                Internal
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-row-odd text-muted">
                <Globe className="w-3 h-3" />
                Public
              </span>
            )}
            <span className={`badge capitalize ${
              article.status === 'published'
                ? 'bg-ok/15 text-ok'
                : 'bg-row-odd text-muted'
            }`}>
              {article.status}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-sm text-muted mb-6 flex-wrap">
          <span>{a.author?.name ?? 'Unknown'}</span>
          <span>·</span>
          <span>{article.createdAt ? new Date(article.createdAt).toLocaleDateString() : ''}</span>
          <span>·</span>
          <span>{article.viewCount} views</span>
          {article.category && (
            <>
              <span>·</span>
              <span className="text-primary">{article.category}</span>
            </>
          )}
        </div>

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-6">
            {article.tags.map((tag: string) => (
              <span key={tag} className="badge bg-row-odd text-muted">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="prose prose-surface max-w-none whitespace-pre-wrap text-fg leading-relaxed">
          {article.content}
        </div>

        {/* Feedback — only for external articles */}
        {!isInternal && (
          <div className="mt-8 pt-6 border-t border-line">
            <p className="text-sm font-medium text-fg mb-2">Was this article helpful?</p>
            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => feedbackMutation.mutate(true)}
                  disabled={feedbackMutation.isPending}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Yes ({article.helpfulCount})
                </button>
                <button
                  onClick={() => feedbackMutation.mutate(false)}
                  disabled={feedbackMutation.isPending}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <ThumbsDown className="w-4 h-4" />
                  No ({article.notHelpfulCount})
                </button>
              </div>
            </div>
          </div>
        )}
      </article>

      {/* Internal Notes — staff only */}
      {isStaff && (
        <div className="max-w-3xl card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-warn" />
            <h2 className="section-title text-warn">Internal Notes</h2>
            <span className="text-xs text-muted ml-1">(staff only)</span>
          </div>

          {(notes as ArticleNote[]).length === 0 ? (
            <p className="text-sm text-muted italic">No internal notes yet.</p>
          ) : (
            <div className="space-y-3">
              {(notes as ArticleNote[]).map((note) => {
                const noteAuthor = (note as any).author;
                const isOwn = (note.authorId === ((user as any)?.id)) || (noteAuthor?.id === (user as any)?.id);
                return (
                  <div key={note.id} className="flex gap-3 group">
                    <div className="w-7 h-7 rounded-full bg-warn/15 flex items-center justify-center shrink-0 text-xs font-semibold text-warn mt-0.5">
                      {noteAuthor?.name?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                    <div className="flex-1 bg-warn/10 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-fg">
                          {noteAuthor?.name ?? 'Staff'}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted">
                            {new Date(note.createdAt).toLocaleString()}
                          </span>
                          {(isOwn || isAdmin) && (
                            <button
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-muted hover:text-err"
                              onClick={() => deleteNoteMutation.mutate(note.id)}
                              title="Delete note"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-fg whitespace-pre-wrap">{note.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add note form */}
          <div className="flex gap-2 pt-1">
            <textarea
              className="input flex-1 text-sm resize-y"
              rows={2}
              placeholder="Add an internal note..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && noteContent.trim()) {
                  e.preventDefault();
                  addNoteMutation.mutate();
                }
              }}
            />
            <button
              className="btn btn-primary self-end inline-flex items-center gap-2"
              disabled={!noteContent.trim() || addNoteMutation.isPending}
              onClick={() => addNoteMutation.mutate()}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Add Note</span>
            </button>
          </div>
          <p className="text-xs text-muted">Ctrl+Enter to submit</p>
        </div>
      )}
    </div>
  );
}
